import {
  createStackNavigator,
  StackScreenProps,
} from '@react-navigation/stack';
import { useEffect } from 'react';
import { Platform } from 'react-native';

import MainHeader from '@components/Headers/MainHeader';
import { MyTasksScreen } from '@components/MyTasks/MyTasksScreen';
import CreateTask from '@components/Tasks/CreateTask';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  Invitation,
  Task,
  useInvitationDetailsLazyQuery,
} from '@graphql/generated';
import { Tasks } from '@screens/Tasks/Tasks.web';

type TasksTabStackParamsList = {
  tasks?: { taskId?: Task['id'] };
  'task-create': undefined;
};

export type TasksTabStackScreenProps<T extends keyof TasksTabStackParamsList> =
  StackScreenProps<TasksTabStackParamsList, T>;

const Stack = createStackNavigator<TasksTabStackParamsList>();

const myTasksHeader = () => <MainHeader location='My Tasks' />;

const TasksTabStack: React.FC = () => {
  const {
    idTaskDetailOpen,
    setIdTaskDetailOpen,
    setIsTaskWebPanelOpen,
    setTaskNameOfTaskDetailOpen,
    setProjectNameOfTaskDetailOpen,
  } = useWebDrawer();

  const [getTaskInviteDetails, { data: taskInviteDetailsData }] =
    useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    // Skip if the view-only flow is active — pending_open_task_panel_id takes
    // priority and will clear this key, so don't set newly_joined_task_id first.
    if (window.sessionStorage.getItem('pending_open_task_panel_id')) return;
    const pendingTaskId = window.sessionStorage.getItem(
      'post_invite_open_task_id'
    );
    if (pendingTaskId) {
      // For automatically accept task invitations
      window.sessionStorage.setItem('newly_joined_task_id', pendingTaskId);
      window.sessionStorage.removeItem('post_invite_open_task_id');
      setIdTaskDetailOpen(pendingTaskId);
      setIsTaskWebPanelOpen(true);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const taskId = window.sessionStorage.getItem('pending_open_task_panel_id');
    if (taskId) {
      // Open task panel without the newly_joined_task_id side-effect.
      // Used when an already-authenticated user clicks a task invite link
      // directly — they are viewing the task, not joining it.
      // Also clear post_invite_open_task_id / newly_joined_task_id in case
      // they are stale from a prior flow (e.g. inherited opener tab) so the
      // first effect does not fire and show incorrect join-guidance UI.
      window.sessionStorage.removeItem('pending_open_task_panel_id');
      window.sessionStorage.removeItem('post_invite_open_task_id');
      window.sessionStorage.removeItem('newly_joined_task_id');
      setIdTaskDetailOpen(taskId);
      setIsTaskWebPanelOpen(true);

      // TA-10467: this is the task-share-link path — unlike a Chat task tag
      // (which already has the task object loaded and sets the name
      // fallback synchronously), we only have a bare taskId here. If the
      // invite carried a token, resolve invitationDetails to populate the
      // task/project name shown while the "Request to Join" fallback is up.
      const inviteToken = window.sessionStorage.getItem(
        'pending_open_task_panel_invite_token'
      );
      window.sessionStorage.removeItem('pending_open_task_panel_invite_token');
      if (inviteToken) {
        getTaskInviteDetails({
          variables: {
            token: inviteToken,
            type: Invitation.Task,
            entityId: taskId,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    const task = taskInviteDetailsData?.invitationDetails?.task;
    if (!task) return;
    // Guard against a race with the user closing/switching tasks before this
    // resolves — only apply the fallback name to the task it was fetched for.
    if (task.id !== idTaskDetailOpen) return;
    setTaskNameOfTaskDetailOpen(task.name ?? '');
    setProjectNameOfTaskDetailOpen(task.projectName ?? '');
  }, [taskInviteDetailsData, idTaskDetailOpen]);

  return (
    <Stack.Navigator
      screenOptions={{
        header: myTasksHeader,
        headerShown: Platform.OS !== 'web',
      }}>
      <Stack.Screen
        name='tasks'
        component={Platform.OS === 'web' ? Tasks : MyTasksScreen}
      />
      <Stack.Screen name='task-create' component={CreateTask} />
    </Stack.Navigator>
  );
};

export default TasksTabStack;
